import { Player } from '../entities/Player';
import { ScoreSystem } from '../systems/ScoreSystem';
import { ChainSystem } from '../systems/ChainSystem';
import { COLORS } from '../config/settings';

export class HUD {
  private canvas: HTMLCanvasElement;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
  }

  public render(
    ctx: CanvasRenderingContext2D,
    player: Player,
    scoreSystem: ScoreSystem,
    chainSystem: ChainSystem,
    region: number,
    wave: number
  ): void {
    // Top bar background (taller for larger HUD fonts)
    const topBarHeight = 120;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(0, 0, this.canvas.width, topBarHeight);

    // Radio button (top right corner) - REMOVED
    // this.renderRadioButton(ctx, this.canvas.width - 120, 10);

    // All HUD elements centered with even spacing
    const centerX = this.canvas.width / 2;
    const yPos = 40;
    // Calculate even spacing: divide canvas into 4 equal parts, elements centered at 1/4, 2/4, 3/4
    const spacing = this.canvas.width / 4;
    
    // Hands (left - centered at 1/4) — scoreSystem now renders as two-line label/value
    scoreSystem.render(ctx, spacing, yPos);

    // Wave info (center - centered at 2/4) — two-line label/value
    this.renderWaveInfo(ctx, region, wave, centerX, yPos);

    // Lives and HP (right - centered at 3/4) — two-line label/value
    this.renderLivesAndHP(ctx, player, spacing * 3, yPos);

    // Chain progress: place bottom-left, above mobile controls (clear of HUD)
    const padding = 12;
    const isTouch = (typeof window !== 'undefined') && (('ontouchstart' in window) || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) || window.innerWidth <= 700);
    const safeBottom = (typeof window !== 'undefined') ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom') || '0') || 0 : 0;
    const controlsHeight = isTouch ? (150 + safeBottom) : 20;
    const scaleOverride = isTouch ? 0.95 : 1.0;
    const size = chainSystem.getBarSize(scaleOverride);
    const chainX = padding;
    const chainY = this.canvas.height - controlsHeight - size.height - padding;
    chainSystem.render(ctx, chainX, chainY, scaleOverride);
  }

  private renderLivesAndHP(ctx: CanvasRenderingContext2D, player: Player, x: number, y: number): void {
    ctx.textAlign = 'center'; // Center align like Wave for consistent spacing
    ctx.textBaseline = 'top';
    
    // Lives: label + number (two-line)
    ctx.font = 'bold 18px "Courier New"';
    ctx.fillStyle = COLORS.TEXT;
    ctx.fillText('LIVES', x, y);
    ctx.font = 'bold 36px "Courier New"';
    ctx.fillText(`${player.getLives()}`, x, y + 22);

    // Shield indicator (smaller label + value)
    if (player.hasShield()) {
      ctx.font = 'bold 16px "Courier New"';
      ctx.fillStyle = COLORS.PRIMARY;
      ctx.fillText('SHIELD', x, y + 60);
      ctx.font = 'bold 20px "Courier New"';
      ctx.fillText('ON', x, y + 80);
      ctx.fillStyle = COLORS.TEXT;
    }
  }

  // Removed drawHeart method - no longer using redundant heart icons

  private renderWaveInfo(ctx: CanvasRenderingContext2D, region: number, wave: number, x: number, y: number): void {
    ctx.fillStyle = COLORS.TEXT; // Same color as Hands and Lives
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    // Wave: label + number
    ctx.font = 'bold 18px "Courier New"';
    ctx.fillText('WAVE', x, y);
    ctx.font = 'bold 36px "Courier New"';
    ctx.fillText(`${wave}`, x, y + 22);
  }

  public renderPowerUpNotification(ctx: CanvasRenderingContext2D, powerUpName: string, timer: number): void {
    if (timer <= 0) return;

    const alpha = Math.min(timer, 1);
    ctx.globalAlpha = alpha;

    const topBarHeight = 120;
    const x = this.canvas.width / 2;
    const y = topBarHeight + 18; // top-center, just below HUD bar

    // Popup scaling (smaller on touch/mobile)
    const isTouch = (typeof window !== 'undefined') && (('ontouchstart' in window) || window.innerWidth <= 700);
    const popupScale = isTouch ? 0.6 : 0.9;

    // Background (scaled)
    const bgW = Math.round(400 * popupScale);
    const bgH = Math.round(80 * popupScale);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
    ctx.fillRect(x - bgW / 2, y - Math.round(bgH / 2) + 6, bgW, bgH);

    // Text
    ctx.font = `bold ${Math.round(24 * popupScale)}px "Courier New"`;
    ctx.fillStyle = '#00ff41';
    ctx.textAlign = 'center';
    ctx.fillText(`Power-Up: ${powerUpName}`, x, y + Math.round(8 * popupScale));

    ctx.globalAlpha = 1;
  }

  public renderWaveComplete(ctx: CanvasRenderingContext2D): void {
    const topBarHeight = 120;
    const x = this.canvas.width / 2;
    const y = topBarHeight + 36; // top-center area
    const isTouch = (typeof window !== 'undefined') && (('ontouchstart' in window) || window.innerWidth <= 700);
    const popupScale = isTouch ? 0.6 : 0.9;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    const boxH = Math.round(80 * popupScale);
    ctx.fillRect(0, y - Math.round(boxH / 2), this.canvas.width, boxH);

    ctx.font = `bold ${Math.round(48 * popupScale)}px "Courier New"`;
    ctx.fillStyle = COLORS.SUCCESS;
    ctx.textAlign = 'center';
    ctx.fillText('WAVE COMPLETE!', x, y - Math.round(8 * popupScale));

    ctx.font = `${Math.round(20 * popupScale)}px "Courier New"`;
    ctx.fillStyle = COLORS.TEXT;
    ctx.fillText('Get ready for the next wave...', x, y + Math.round(24 * popupScale));
  }

  public renderBossWarning(ctx: CanvasRenderingContext2D, bossName: string): void {
    const topBarHeight = 120;
    const x = this.canvas.width / 2;
    const y = topBarHeight + 36; // top-center area for warnings
    const isTouch = (typeof window !== 'undefined') && (('ontouchstart' in window) || window.innerWidth <= 700);
    const popupScale = isTouch ? 0.6 : 0.9;

    ctx.fillStyle = 'rgba(128, 0, 0, 0.8)';
    const boxH = Math.round(100 * popupScale);
    ctx.fillRect(0, y - Math.round(boxH / 2), this.canvas.width, boxH);

    ctx.font = `bold ${Math.round(48 * popupScale)}px "Courier New"`;
    ctx.fillStyle = COLORS.DANGER;
    ctx.textAlign = 'center';
    ctx.fillText('WARNING!', x, y - Math.round(8 * popupScale));

    ctx.font = `bold ${Math.round(32 * popupScale)}px "Courier New"`;
    ctx.fillStyle = COLORS.ACCENT;
    ctx.fillText(bossName, x, y + Math.round(18 * popupScale));
  }
}
